import { Placement } from "@floating-ui/react-dom";
import {
  Popover,
  ListBox,
  InteractiveContainer,
  PlainButton,
} from "@jugl-web/ui-components/cross-platform";
import { useTranslations, cx } from "@jugl-web/utils";
import { useMemo } from "react";
import countries from "countries-phone-masks";
import { Country } from "@jugl-web/ui-components/cross-platform/PhoneInput/types";
import { CountryPickerValue } from "./types";
import { ReactComponent as CloseIcon } from "./assets/close.svg";

const countriesMasks = countries as unknown as Country[];

export type CountryPickerProps = {
  children: React.ReactNode | ((isOpen: boolean) => React.ReactNode);
  countryIso?: string;
  placement?: Placement;
  isDisabled?: boolean;
  className?: string;
  onSelect: (country: CountryPickerValue) => void;
};

export const CountryPicker: React.FC<CountryPickerProps> = ({
  children,
  countryIso,
  placement = "bottom",
  isDisabled,
  className,
  onSelect,
}) => {
  const { t } = useTranslations();

  const preparedCountries = useMemo(() => {
    const newArray = [...countriesMasks];

    let usaCountry: Country | undefined;
    let indiaCountry: Country | undefined;
    let malaysiaCountry: Country | undefined;

    const filteredArray = newArray.filter((country) => {
      if (country.iso === "US") {
        usaCountry = country;
        return false;
      }
      if (country.iso === "IN") {
        indiaCountry = country;
        return false;
      }
      if (country.iso === "MY") {
        malaysiaCountry = country;
        return false;
      }
      return true;
    });

    if (malaysiaCountry) filteredArray.unshift(malaysiaCountry); // Malaysia third
    if (indiaCountry) filteredArray.unshift(indiaCountry); // India second
    if (usaCountry) filteredArray.unshift(usaCountry); // USA first

    return filteredArray.map((el) => ({
      id: el.iso,
      value: el,
    }));
  }, []);

  return (
    <Popover
      placement={placement}
      className={cx("p-6", className)}
      isDisabled={isDisabled}
      renderTrigger={({ Trigger, triggerRef, isOpen }) => (
        <Trigger ref={triggerRef} as={InteractiveContainer}>
          {typeof children === "function" ? children(isOpen) : children}
        </Trigger>
      )}
    >
      {({ onClose }) => (
        <div className="flex flex-col gap-4">
          <span className="font-secondary flex items-center justify-between font-semibold text-[#383838]">
            <div>
              {t({
                id: "country-picker-component.select-country",
                defaultMessage: "Select country",
              })}
            </div>
            <PlainButton onClick={onClose}>
              <CloseIcon />
            </PlainButton>
          </span>
          <ListBox
            selectionBehavior={{ mode: "single", canToggle: false }}
            isHeightFixed
            className="w-[375px]"
            itemSize="sm"
            items={preparedCountries}
            renderLabel={(item) => (
              <div className="font-secondary">{`${item.value.name} (${item.value.iso})`}</div>
            )}
            renderSearchLabel={(item) =>
              `${item.value.name} (${item.value.iso})`
            }
            renderStartIcon={(item) => (
              <img
                src={item.value.flag}
                alt={item.value.iso}
                loading="lazy"
                className="h-[20px] w-[25px]"
              />
            )}
            maxVisibleItems={6}
            hasSearch
            onSelect={(item) => {
              onSelect(item.value);
              onClose();
            }}
            defaultSelectedIds={countryIso ? [countryIso] : undefined}
          />
        </div>
      )}
    </Popover>
  );
};
